import React, { useContext, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ApplicationContext } from 'App/context'
import { TBreadcrumbItemProps, TPageProps } from 'App/util/appRoutes'
import * as _ from 'lodash'
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { Breadcrumb } from 'antd';
import { useSelector } from 'react-redux'
import { TDefaultSagas } from 'App/redux/interface'

const BreadcrumbItem = ({ page }: { page: TPageProps }) => {
  const breadcrumb = useSelector((state: TDefaultSagas) => state.breadcrumb)

  const getPageTitle = () => {
    if (page.breadcrumbProps) {
      if (page.breadcrumbProps.lookupKey) {
        if (breadcrumb.map.has(page.breadcrumbProps.lookupKey)) {
          return breadcrumb.map.get(page.breadcrumbProps.lookupKey)
        }
      }
      return page.breadcrumbProps.title
    }

    if (page.navProps) {
      return page.navProps.title
    }

    return '...'
  }

  const pageTitle = getPageTitle()

  return page.navProps?.icon ? (
    <>
      <LegacyIcon type={page.navProps.icon} style={{ marginRight: 10 }} />
      <span>{pageTitle}</span>
    </>
  ) : (
      <>{pageTitle}</>
    );
}

const BreadcrumbRoute = () => {
  const location = useLocation()
  const ctx = useContext(ApplicationContext)
  const [items, setItems] = useState<TPageProps[]>([])

  const pushPage = (breadcrumbPath: TPageProps[], page: TPageProps, rootPage?: TPageProps) => {
    const copy = {
      ...page,
      routeProps: {
        ...page.routeProps,
        path: rootPage
          ? `${rootPage.routeProps.path}${page.routeProps.path}`
          : page.routeProps.path,
      },
    }

    // console.log('pushPage', copy);

    if (copy.breadcrumbProps || copy.navProps) {
      breadcrumbPath.push(copy)
    }

    if (copy.routeProps.path !== location.pathname && copy.children?.length) {
      // eslint-disable-next-line no-use-before-define
      breadcrumbPath.push(...getBreadcrumbPath(copy))
    }

    return breadcrumbPath
  }

  const getBreadcrumbPath = (rootPage?: TPageProps) => {
    let breadcrumbPath: TPageProps[] = []

    if (rootPage) {
      // console.log('getBreadcrumbPath with rootPage', rootPage);

      const locationParts = location.pathname.split('/')
      // console.log('--> location.parts', locationParts);

      rootPage.children?.find((p) => {
        const childPathname = `${rootPage.routeProps.path}${p.routeProps.path}`
        const childLocationParts = childPathname.split('/')
        // console.log('--> checking child', childPathname, childLocationParts);

        if (locationParts.length >= childLocationParts.length) {
          const childLocationPartsIndex = childLocationParts.length - 1
          // console.log('--> potential match?', childLocationParts[childLocationPartsIndex], locationParts[childLocationPartsIndex]);
          if (
            childLocationParts[childLocationPartsIndex] === locationParts[childLocationPartsIndex]
          ) {
            breadcrumbPath = pushPage(breadcrumbPath, p, rootPage)
            return true
          }
          if (childLocationParts[childLocationPartsIndex].startsWith(':')) {
            const pageCopy = _.cloneDeep(p)
            pageCopy.breadcrumbProps = {
              title: pageCopy.breadcrumbProps?.title || '',
              component: pageCopy.breadcrumbProps?.component || undefined,
              lookupKey: locationParts.slice(0, childLocationParts.length).join('/'),
            }
            breadcrumbPath = pushPage(breadcrumbPath, pageCopy, rootPage)
            return true
          }
        }
        return false
      })
    } else {
      // console.log('getBreadcrumbPath without rootPage');
      const pages = ctx.pages.slice() // create a copy of pages
      const foundRoot = pages.find((p) => location.pathname.startsWith(p.routeProps.path as string))
      if (foundRoot) {
        breadcrumbPath = pushPage(breadcrumbPath, foundRoot)
      }
    }

    return breadcrumbPath
  }

  useEffect(() => {
    // console.log('pathname or breadcrumb map changed', location.pathname, ctx.breadcrumbMap);
    setItems(getBreadcrumbPath())
  }, [location.pathname])

  // console.log('breadcrumbRoute render', ctx.breadcrumbMap);

  return items.length ? (
    <Breadcrumb>
      {items.map((page, index) => {
        if (index !== items.length - 1) {
          if(page.breadcrumbProps?.component != undefined){
            var breadcrumbItemProps: TBreadcrumbItemProps = {
              active: false,
              page
            }
            return React.createElement<TBreadcrumbItemProps>(page.breadcrumbProps?.component, breadcrumbItemProps);
          }

          return (
            <Breadcrumb.Item key={`breadcrumb-${page.name}`}>
              <Link to={page.routeProps.path as string}>
                <BreadcrumbItem page={page} />
              </Link>
            </Breadcrumb.Item>
          )
        }

        if(page.breadcrumbProps?.component != undefined){
          var breadcrumbItemProps: TBreadcrumbItemProps = {
            active: true,
            page
          }
          return React.createElement<TBreadcrumbItemProps>(page.breadcrumbProps?.component, breadcrumbItemProps);
        }

        return (
          <Breadcrumb.Item key={`breadcrumb-${page.name}`}>
            <BreadcrumbItem page={page} />
          </Breadcrumb.Item>
        )
      })}
    </Breadcrumb>
  ) : null
}

export default BreadcrumbRoute
